import {useCallback, useEffect, useMemo, useState} from 'react';

import {AuthSession} from '../App';
import {ChipKind, TenantChip} from '../lib/aggregate';
import {
    AccessRequest,
    Me,
    RequestStatus,
    SessionExpiredError,
    TempAccessRequest,
    TwingateUnreachableError,
    decideAccessRequest,
    fetchMyRequests,
    fetchPendingRequests,
} from '../lib/api';
import {kindHeading} from '../lib/chip-style';
import {GrantView} from '../lib/grant-view';
import {HistoryRow, HistoryStatus, buildHistoryRows, syncSourceLabel} from '../lib/history-row';
import {makeTenantChip, makeUserChip, timeLeftLabel} from '../lib/request-chips';

import {AccessChip} from './AccessChip';
import {MultiSelectFilter} from './MultiSelectFilter';
import {RequestDetailDrawer} from './RequestDetailDrawer';
import {Spinner} from './Spinner';
import {UserChip} from './UserChip';

export type Range = '24h' | '7d';

export const PRESET_MS: Record<Range, number> = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
};

/** State that App owns — survives a nav away to Grants and back. */
export interface RequestsPageState {
    loading: boolean;
    twingateDown: boolean;
    error?: string;
    /** CloudWatch grant events (one row per (kind, shardId, user)). */
    data?: TempAccessRequest[];
    /**
     * DDB-backed AccessRequest rows in the same window. Joined with `data`
     * on the page to produce the unified history view (status pill +
     * `synced from <source>` for log-only rows).
     */
    accessRequests?: AccessRequest[];
    since: number;
    until: number;
    /** Highlighted preset; undefined when the window came from custom date inputs. */
    preset?: Range;
}

export type RequestsLoadFn = (since: number, until: number, preset?: Range) => void;

const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

type SubTab = 'mine' | 'pending' | 'history';

interface Props {
    session: AuthSession;
    me: Me;
    grants: GrantView[];
    state: RequestsPageState;
    onLoad: RequestsLoadFn;
}

/**
 * Three sub-tabs: My (user only), Pending (admin only), History (everyone).
 * Default sub-tab depends on role: admins land in History (the everyday
 * "what happened lately" view); users have no tabs and see their own My
 * requests list directly.
 *
 * History keeps the existing CloudWatch Insights flow — its state is lifted
 * into App because it's the expensive one to refetch. Pending and My are
 * lifted to this component (rather than to App) so the tab badges can show
 * counts without each tab needing to be mounted; the fetch is cheap enough
 * that running both on every page load is fine.
 */
export function RequestsPage({session, me, grants, state, onLoad}: Props) {
    const isAdmin = me.role === 'admin';
    const [tab, setTab] = useState<SubTab>(isAdmin ? 'history' : 'mine');

    // `me` lands asynchronously, so on the first render isAdmin can be false
    // even for admins. Re-snap the active tab to a valid value once the role
    // resolves — otherwise admins land on 'mine' which isn't in their tab list
    // and end up with no visually-active tab.
    useEffect(() => {
        if (isAdmin && tab === 'mine') {
            setTab('history');
        } else if (!isAdmin && tab !== 'mine') {
            setTab('mine');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    // Lift Pending/My fetches up here so the tab-badge counts stay accurate
    // even when the user is on the other tab.
    const pending = useAccessRequestList(session, isAdmin ? 'pending' : null);
    const mine = useAccessRequestList(session, !isAdmin ? 'mine' : null);

    // Decide what the page-head Reload button does based on the active tab.
    let onReload: () => void;
    let reloadLoading: boolean;
    if (tab === 'history') {
        onReload = () => onLoad(state.since, state.until, state.preset);
        reloadLoading = state.loading;
    } else if (tab === 'pending') {
        onReload = pending.reload;
        reloadLoading = pending.loading;
    } else {
        onReload = mine.reload;
        reloadLoading = mine.loading;
    }

    return (
        <main className="page">
            <div className="page-head">
                <h1>Requests</h1>
                <div className="spacer" />
                <button
                    type="button"
                    className="btn-reload"
                    onClick={onReload}
                    disabled={reloadLoading}
                >
                    {reloadLoading ? 'Loading…' : 'Reload'}
                </button>
            </div>
            <div className="page-subhead">
                {isAdmin
                    ? 'Temp-access grant history and pending approval requests from users.'
                    : 'Your access requests, newest first. Updates show up here when an admin acts on a request.'}
            </div>

            {isAdmin ? (
                <>
                    <div className="tabs" role="tablist">
                        <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
                            History <span className="count">{state.data?.length ?? 0}</span>
                        </TabButton>
                        <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>
                            Pending approvals{' '}
                            <span className="count">{pending.data?.length ?? 0}</span>
                        </TabButton>
                    </div>
                    {tab === 'history' && (
                        <HistoryTab
                            session={session}
                            grants={grants}
                            state={state}
                            onLoad={onLoad}
                        />
                    )}
                    {tab === 'pending' && (
                        <PendingApprovalsTab session={session} grants={grants} list={pending} />
                    )}
                </>
            ) : (
                <MyRequestsTab grants={grants} list={mine} />
            )}
        </main>
    );
}

// -- Shared fetch hook for Pending / My --------------------------------------

interface AccessRequestListState {
    data?: AccessRequest[];
    loading: boolean;
    error?: string;
    twingateDown: boolean;
    reload: () => void;
    /** Optimistic mutation — used by PendingApprovalsTab on approve/deny. */
    setData: React.Dispatch<React.SetStateAction<AccessRequest[] | undefined>>;
}

/**
 * Shared fetch state machine for Pending Approvals and My Requests. Pass
 * `scope = null` to disable the fetch (e.g., when the current role doesn't
 * own this scope).
 */
function useAccessRequestList(
    session: AuthSession,
    scope: 'mine' | 'pending' | null
): AccessRequestListState {
    const [data, setData] = useState<AccessRequest[] | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [twingateDown, setTwingateDown] = useState(false);

    const reload = useCallback(async () => {
        if (scope === null) {
            return;
        }
        setLoading(true);
        setError(undefined);
        setTwingateDown(false);
        try {
            const fetcher = scope === 'pending' ? fetchPendingRequests : fetchMyRequests;
            const rows = await fetcher(session.idToken);
            setData(rows);
        } catch (err) {
            if (err instanceof SessionExpiredError) {
                return;
            }
            if (err instanceof TwingateUnreachableError) {
                setTwingateDown(true);
            } else {
                setError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            setLoading(false);
        }
    }, [session.idToken, scope]);

    useEffect(() => {
        reload();
    }, [reload]);

    return {data, loading, error, twingateDown, reload, setData};
}

function TabButton({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            role="tab"
            className={`tab${active ? ' active' : ''}`}
            onClick={onClick}
        >
            {children}
        </button>
    );
}

// -- "My requests" tab -------------------------------------------------------

function MyRequestsTab({grants, list}: {grants: GrantView[]; list: AccessRequestListState}) {
    if (list.twingateDown) {
        return <TwingatePrompt onReload={list.reload} loading={list.loading} />;
    }

    return (
        <AccessRequestList
            requests={list.data ?? []}
            loading={list.loading}
            error={list.error}
            grants={grants}
            emptyMessage="You haven't requested any access yet — use the Request access button in the sidebar."
        />
    );
}

// -- "Pending approvals" tab (admin) -----------------------------------------

function PendingApprovalsTab({
    session,
    grants,
    list,
}: {
    session: AuthSession;
    grants: GrantView[];
    list: AccessRequestListState;
}) {
    // Local in-flight tracking — disables a row's Approve/Deny buttons while
    // its decide() call is round-tripping.
    const [deciding, setDeciding] = useState<Set<string>>(new Set());

    const decide = useCallback(
        async (id: string, decision: 'approve' | 'deny') => {
            setDeciding(prev => new Set(prev).add(id));
            try {
                await decideAccessRequest(session.idToken, id, decision);
                // Optimistically remove the row from the local list — it's no
                // longer pending.
                list.setData(prev => prev?.filter(r => r.id !== id));
            } catch {
                // Surface failures by reloading the list — the server is the
                // source of truth and the decide call may have succeeded.
                list.reload();
            } finally {
                setDeciding(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        },
        [session.idToken, list]
    );

    if (list.twingateDown) {
        return <TwingatePrompt onReload={list.reload} loading={list.loading} />;
    }

    return (
        <AccessRequestList
            requests={list.data ?? []}
            loading={list.loading}
            error={list.error}
            grants={grants}
            emptyMessage="No pending requests."
            onApprove={(id: string) => decide(id, 'approve')}
            onDeny={(id: string) => decide(id, 'deny')}
            inFlight={deciding}
        />
    );
}

// -- AccessRequest list (shared by My + Pending) -----------------------------

interface AccessRequestListProps {
    requests: AccessRequest[];
    loading: boolean;
    error?: string;
    grants: GrantView[];
    emptyMessage: string;
    onApprove?: (id: string) => void;
    onDeny?: (id: string) => void;
    inFlight?: Set<string>;
}

function AccessRequestList(props: AccessRequestListProps) {
    const {requests, loading, error, grants, emptyMessage, onApprove, onDeny, inFlight} = props;

    const tenantNameOf = useCallback(
        (id: string) => grants.find(g => g.resource === id)?.resourceName,
        [grants]
    );

    return (
        <div className="card">
            {loading ? (
                <Spinner label="Loading requests…" />
            ) : error ? (
                <div className="empty">
                    <h3>Failed to load</h3>
                    <div>{error}</div>
                </div>
            ) : requests.length === 0 ? (
                <div className="empty">
                    <div>{emptyMessage}</div>
                </div>
            ) : (
                <table className="tbl">
                    <thead>
                        <tr>
                            <th className="col-when">Created</th>
                            <th>Requester</th>
                            <th>Access</th>
                            <th>Reason</th>
                            <th>Status</th>
                            {(onApprove || onDeny) && <th className="col-actions">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {requests.map(r => (
                            <RequestRow
                                key={r.id}
                                request={r}
                                tenantNameOf={tenantNameOf}
                                onApprove={onApprove}
                                onDeny={onDeny}
                                inFlight={inFlight?.has(r.id) ?? false}
                            />
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function RequestRow({
    request,
    tenantNameOf,
    onApprove,
    onDeny,
    inFlight,
}: {
    request: AccessRequest;
    tenantNameOf: (id: string) => string | undefined;
    onApprove?: (id: string) => void;
    onDeny?: (id: string) => void;
    inFlight: boolean;
}) {
    const daChips = request.dataAccess
        ? request.dataAccess.tenantIds.map(id =>
              makePendingTenantChip(id, 'data-access', tenantNameOf(id))
          )
        : null;
    const impChips = request.impersonation
        ? request.impersonation.tenantIds.map(id =>
              makePendingTenantChip(id, 'imp-decisions', tenantNameOf(id))
          )
        : null;
    const impAiChips = request.impAi
        ? request.impAi.tenantIds.map(id => makePendingTenantChip(id, 'imp-ai', tenantNameOf(id)))
        : null;

    return (
        <tr>
            <td className="col-when">
                <div className="when-cell">
                    <div>{formatRelative(request.createdAt)}</div>
                    <div className="when-abs">{formatAbsolute(request.createdAt)}</div>
                </div>
            </td>
            <td>{request.requesterEmail}</td>
            <td>
                <div className="access-summary">
                    {daChips && (
                        <div className="access-line">
                            <span className="kind-tag kind-da">DataAccess</span>
                            <div className="access-chips">
                                {daChips.map(c => (
                                    <AccessChip key={`da-${c.resource}`} chip={c} hideBadge />
                                ))}
                            </div>
                        </div>
                    )}
                    {impAiChips && (
                        <div className="access-line">
                            <span className="kind-tag kind-imp-ai">{kindHeading('imp-ai')}</span>
                            <div className="access-chips">
                                {impAiChips.map(c => (
                                    <AccessChip key={`impai-${c.resource}`} chip={c} hideBadge />
                                ))}
                            </div>
                        </div>
                    )}
                    {impChips && (
                        <div className="access-line">
                            <span className="kind-tag kind-imp-dec">{kindHeading('imp-decisions')}</span>
                            <div className="access-chips">
                                {impChips.map(c => (
                                    <AccessChip key={`imp-${c.resource}`} chip={c} hideBadge />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </td>
            <td className="reason-cell">{request.reason}</td>
            <td>
                <StatusChip status={request.status} />
                {request.decidedBy && (
                    <div className="when-abs">
                        by {request.decidedBy}
                        {request.decidedAt ? ` · ${formatAbsolute(request.decidedAt)}` : ''}
                    </div>
                )}
            </td>
            {(onApprove || onDeny) && (
                <td className="col-actions">
                    {onApprove && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={inFlight}
                            onClick={() => onApprove(request.id)}
                        >
                            Approve
                        </button>
                    )}
                    {onDeny && (
                        <button
                            type="button"
                            className="btn btn-danger"
                            disabled={inFlight}
                            onClick={() => onDeny(request.id)}
                        >
                            Deny
                        </button>
                    )}
                </td>
            )}
        </tr>
    );
}

/**
 * Build a TenantChip for a pending request — the tenant hasn't been granted yet,
 * so there's no expiry. We mark `permanent: true` purely to skip the time-based
 * styling tier; the `hideBadge` prop on AccessChip suppresses the ∞ badge.
 */
function makePendingTenantChip(
    tenantId: string,
    kind: ChipKind,
    tenantName: string | undefined
): TenantChip {
    return {
        resource: tenantId,
        resourceName: tenantName,
        resourceIds: [tenantId],
        kind,
        permanent: true,
        daysRemaining: Number.POSITIVE_INFINITY,
        hoursRemaining: Number.POSITIVE_INFINITY,
        fromBastion: false,
        grants: [],
    };
}

function StatusChip({status}: {status: RequestStatus}) {
    return <span className={`status-chip status-${status}`}>{status}</span>;
}

/**
 * Status cell for the History tab. Renders one of:
 *   - the request's status pill + admin breadcrumb (decidedBy / decidedAt)
 *   - a `synced from <source>` pill for log events without a matching
 *     AccessRequest (legacy Slack `/access-temp`, direct DDB grants)
 */
function HistoryStatusCell({row}: {row: HistoryRow}) {
    if (row.status === 'synced') {
        return (
            <span className="status-chip status-synced" title="No AccessRequest match">
                synced from {syncSourceLabel(row.source)}
            </span>
        );
    }
    return (
        <>
            <StatusChip status={row.status} />
            {row.decidedBy && (
                <div className="when-abs">
                    by {row.decidedBy}
                    {row.decidedAtIso
                        ? ` · ${formatAbsolute(Date.parse(row.decidedAtIso))}`
                        : ''}
                </div>
            )}
        </>
    );
}

// -- History tab (CloudWatch Insights — pre-Phase 3 behavior) ----------------

function HistoryTab({
    session,
    grants,
    state,
    onLoad,
}: {
    session: AuthSession;
    grants: GrantView[];
    state: RequestsPageState;
    onLoad: RequestsLoadFn;
}) {
    const [search, setSearch] = useState('');
    const [tenants, setTenants] = useState<Set<string>>(new Set());
    const [kindFilter, setKindFilter] = useState<'all' | ChipKind>('all');
    const [statusFilter, setStatusFilter] = useState<HistoryStatus | 'all'>('all');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [selectedRequest, setSelectedRequest] = useState<TempAccessRequest | null>(null);

    const emailByUsername = useMemo(() => {
        const m = new Map<string, string>();
        for (const g of grants) {
            const u = (g.raw as {userName?: string} | undefined)?.userName;
            if (u && g.email && !m.has(u)) {
                m.set(u, g.email);
            }
        }
        return m;
    }, [grants]);

    // Build the unified history list by joining CW events with the DDB
    // AccessRequest rows. The join is cheap (linear scan of both arrays);
    // memoize on the three inputs so changing only the filter doesn't recompute.
    const historyRows: HistoryRow[] = useMemo(
        () =>
            buildHistoryRows({
                cwRequests: state.data ?? [],
                accessRequests: state.accessRequests ?? [],
                emailByUsername,
            }),
        [state.data, state.accessRequests, emailByUsername]
    );

    const tenantOptions = useMemo(() => {
        interface Acc {
            label: string;
            name?: string;
            ids: string[];
        }
        const byCanonical = new Map<string, Acc>();
        for (const g of grants) {
            const canon = g.resourceName ?? g.resource;
            let entry = byCanonical.get(canon);
            if (!entry) {
                entry = {label: canon, name: g.resourceName, ids: []};
                byCanonical.set(canon, entry);
            }
            if (!entry.name && g.resourceName) {
                entry.name = g.resourceName;
            }
            if (!entry.ids.includes(g.resource)) {
                entry.ids.push(g.resource);
            }
        }
        const opts = Array.from(byCanonical.entries()).map(([value, e]) => {
            let label: string;
            if (value === '*') {
                label = 'All tenants (wildcard)';
            } else if (value === '?') {
                label = 'Unknown';
            } else if (e.name) {
                label = `${e.name} · ${e.ids.join(', ')}`;
            } else {
                label = value;
            }
            return {value, label};
        });
        opts.sort((a, b) => {
            if (a.value === '*') {
                return -1;
            }
            if (b.value === '*') {
                return 1;
            }
            return a.label.localeCompare(b.label);
        });
        return opts;
    }, [grants]);

    const tenantNameOf = useCallback(
        (resource: string) => grants.find(g => g.resource === resource)?.resourceName,
        [grants]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return historyRows.filter(r => {
            if (kindFilter !== 'all' && r.kind !== kindFilter) {
                return false;
            }
            if (statusFilter !== 'all' && r.status !== statusFilter) {
                return false;
            }
            const name = tenantNameOf(r.shardId);
            if (tenants.size > 0 && !tenants.has(name ?? r.shardId)) {
                return false;
            }
            if (!q) {
                return true;
            }
            return (
                r.username?.toLowerCase().includes(q) ||
                r.email?.toLowerCase().includes(q) ||
                r.shardId.toLowerCase().includes(q) ||
                name?.toLowerCase().includes(q) ||
                r.reason.toLowerCase().includes(q) ||
                r.decidedBy?.toLowerCase().includes(q)
            );
        });
    }, [historyRows, search, tenants, kindFilter, statusFilter, tenantNameOf]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.max(0, Math.min(page, pageCount - 1));
    const pageStart = safePage * pageSize;
    const pageRows = filtered.slice(pageStart, pageStart + pageSize);

    useEffect(() => {
        setPage(0);
    }, [search, tenants, kindFilter, statusFilter, state.since, state.until, pageSize]);

    const [fromDraft, setFromDraft] = useState(() => toDateInput(state.since));
    const [toDraft, setToDraft] = useState(() => toDateInput(state.until));
    useEffect(() => {
        setFromDraft(toDateInput(state.since));
        setToDraft(toDateInput(state.until));
    }, [state.since, state.until]);

    const applyCustom = () => {
        const since = fromDateInput(fromDraft, false);
        const until = fromDateInput(toDraft, true);
        if (since === undefined || until === undefined || since >= until) {
            return;
        }
        onLoad(since, until);
    };

    const windowLabel = state.preset ? `last ${state.preset}` : `${fromDraft} → ${toDraft}`;
    const reload = () => onLoad(state.since, state.until, state.preset);

    if (state.twingateDown) {
        return <TwingatePrompt onReload={reload} loading={state.loading} />;
    }

    return (
        <>
            <div className="toolbar">
                <div className="search">
                    <svg viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                        <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search username, email, tenant, or reason…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <MultiSelectFilter
                    label="Tenant"
                    options={tenantOptions}
                    selected={tenants}
                    onChange={setTenants}
                />

                <div className="filter-pills">
                    <span className="filter-label">Kind</span>
                    <div className="toggle-group">
                        {(
                            [
                                {value: 'all', label: 'All'},
                                {value: 'imp-ai', label: 'Ennabl AI'},
                                {value: 'data-access', label: 'DataAccess'},
                                {value: 'imp-decisions', label: 'Decisions'},
                            ] as const
                        ).map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`chip-toggle${kindFilter === opt.value ? ' on' : ''}`}
                                onClick={() => setKindFilter(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="filter-pills">
                    <span className="filter-label">Status</span>
                    <select
                        className="filter-select"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value as HistoryStatus | 'all')}
                        aria-label="Filter by status"
                    >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="auto-approved">Auto-approved</option>
                        <option value="approved">Approved</option>
                        <option value="denied">Denied</option>
                        <option value="synced">Synced from source</option>
                    </select>
                </div>

                {(['24h', '7d'] as const).map(range => (
                    <button
                        key={range}
                        type="button"
                        className={`chip-toggle${state.preset === range ? ' on' : ''}`}
                        onClick={() => onLoad(Date.now() - PRESET_MS[range], Date.now(), range)}
                        disabled={state.loading && state.preset === range}
                    >
                        {range === '24h' ? 'Last 24h' : `Last ${range}`}
                    </button>
                ))}

                <div className="date-range" aria-label="Custom date range">
                    <input
                        type="date"
                        aria-label="From"
                        value={fromDraft}
                        max={toDraft || undefined}
                        onChange={e => setFromDraft(e.target.value)}
                    />
                    <span className="date-range-sep">→</span>
                    <input
                        type="date"
                        aria-label="To"
                        value={toDraft}
                        min={fromDraft || undefined}
                        max={toDateInput(Date.now())}
                        onChange={e => setToDraft(e.target.value)}
                    />
                    <button
                        type="button"
                        className="chip-toggle"
                        onClick={applyCustom}
                        disabled={state.loading || !fromDraft || !toDraft || fromDraft > toDraft}
                    >
                        Apply
                    </button>
                </div>

            </div>

            <div className="card">
                {state.loading ? (
                    <Spinner
                        label="Loading requests…"
                        sublabel={`CloudWatch Logs Insights (${windowLabel})`}
                    />
                ) : state.error ? (
                    <div className="empty">
                        <h3>Failed to load</h3>
                        <div>{state.error}</div>
                        <button
                            type="button"
                            className="btn btn-text"
                            style={{marginTop: 12}}
                            onClick={reload}
                        >
                            Retry
                        </button>
                    </div>
                ) : pageRows.length === 0 ? (
                    <div className="empty">
                        <h3>No requests</h3>
                        <div>
                            {search
                                ? `Nothing matches "${search}" in the ${windowLabel}.`
                                : `No requests in the ${windowLabel}.`}
                        </div>
                    </div>
                ) : (
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th className="col-when">When</th>
                                <th>User</th>
                                <th>Tenant</th>
                                <th className="col-time-left">Time left</th>
                                <th>Reason</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map(r => {
                                const tenantName = tenantNameOf(r.shardId);
                                // Synthesize a TempAccessRequest-shaped object
                                // for the chip helpers and the drawer. When the
                                // row was matched against a CW event we pass
                                // through the original event so the policy
                                // timestamp / time-left stays accurate; for
                                // unmatched AR rows (denied / pending) we
                                // synthesize with a zero policyTimestamp so
                                // timeLeftLabel falls through to '—'.
                                const cwLike: TempAccessRequest = r.cwRequest ?? {
                                    kind: r.kind,
                                    grantedAt: r.at,
                                    email: r.email,
                                    username: r.username,
                                    grantedBy: r.decidedBy,
                                    shardId: r.shardId,
                                    reason: r.reason,
                                    policyTimestamp: '0',
                                };
                                const userChip = makeUserChip(cwLike, r.email);
                                const tenantChip = makeTenantChip(cwLike, tenantName);
                                return (
                                    <tr
                                        key={r.key}
                                        onClick={() => setSelectedRequest(cwLike)}
                                    >
                                        <td className="col-when">
                                            <div className="when-cell">
                                                <div>{formatRelative(Date.parse(r.at))}</div>
                                                <div className="when-abs">
                                                    {formatAbsolute(Date.parse(r.at))}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="access-chips">
                                                <UserChip chip={userChip} hideBadge />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="access-chips">
                                                <AccessChip chip={tenantChip} hideBadge />
                                            </div>
                                        </td>
                                        <td className="col-time-left">
                                            {timeLeftLabel(cwLike)}
                                        </td>
                                        <td className="reason-cell">{r.reason}</td>
                                        <td>
                                            <HistoryStatusCell row={r} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {!state.loading && pageRows.length > 0 && (
                    <div className="pagination">
                        <div className="rpp">
                            <span>Rows per page</span>
                            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                                {PAGE_SIZES.map(n => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <span className="range">
                            {pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)} of{' '}
                            {filtered.length}
                        </span>
                        <div className="page-btns">
                            <button
                                type="button"
                                className="pg"
                                disabled={safePage === 0}
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                aria-label="Previous page"
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                className="pg"
                                disabled={safePage >= pageCount - 1}
                                onClick={() => setPage(p => p + 1)}
                                aria-label="Next page"
                            >
                                ›
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <RequestDetailDrawer
                open={selectedRequest !== null}
                request={selectedRequest}
                grants={grants}
                session={session}
                onClose={() => setSelectedRequest(null)}
            />
        </>
    );
}

// -- helpers -----------------------------------------------------------------

function TwingatePrompt({onReload, loading}: {onReload: () => void; loading: boolean}) {
    return (
        <div className="twingate-prompt">
            <h2>Подключите Twingate</h2>
            <p>API недоступен — подключись и нажми Reload.</p>
            <button type="button" className="btn btn-primary" onClick={onReload} disabled={loading}>
                Reload
            </button>
        </div>
    );
}

function formatRelative(ts: number): string {
    if (!Number.isFinite(ts)) {
        return '';
    }
    const diff = Date.now() - ts;
    if (diff < 60_000) {
        return 'just now';
    }
    if (diff < 3_600_000) {
        return `${Math.round(diff / 60_000)}m ago`;
    }
    if (diff < 86_400_000) {
        return `${Math.round(diff / 3_600_000)}h ago`;
    }
    return `${Math.round(diff / 86_400_000)}d ago`;
}

function formatAbsolute(ts: number): string {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function toDateInput(ts: number): string {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function fromDateInput(value: string, endOfDay: boolean): number | undefined {
    if (!value) {
        return undefined;
    }
    const [yy, mm, dd] = value.split('-').map(Number);
    if (!yy || !mm || !dd) {
        return undefined;
    }
    const d = endOfDay
        ? new Date(yy, mm - 1, dd, 23, 59, 59, 999)
        : new Date(yy, mm - 1, dd, 0, 0, 0, 0);
    return d.getTime();
}
